import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  effect,
  inject,
  Inject,
  InjectionToken,
  NO_ERRORS_SCHEMA,
  OnInit,
  PLATFORM_ID,
  signal,
  NgZone,
  computed,
  untracked,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ToastModule } from 'primeng/toast';
import { GoogleMapComponent } from './shared/components/google-map/google-map.component';
import { UserHeadersComponent } from './shared/components/headers/user-headers.component';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { MessagingService } from './services/messaging.service';
import { SwUpdateService } from './services/sw-update.service';
import { assets } from './core/constants/assets.const';
import { LiveChatWidgetModule } from '@livechat/widget-angular';
import { TrackingMapComponent } from './shared/components/tracking-map/tracking-map.component';
import { MapWidgetsComponent } from './shared/components/map-widgets/map-widgets.component';
import { DriverEnquiryComponent } from './shared/components/driver-enquiry/driver-enquiry.component';
import { EnquiryService } from './services/enquiries.service';
import { AuthService } from './services/auth.service';
import { DriverEnquiryImmediateComponent } from './shared/components/driver-enquiry-immediate/driver-enquiry-immediate.component';
import { AudioService } from './services/audio.service';
import { GlobalClickService } from './services/global-click.service';
import { Meta, Title } from '@angular/platform-browser';
import { I18nService } from './services/i18n.service';
import { aiService } from './services/ai.service';
import { MalayalamPiperTtsService } from './services/malayalam-piper-tts.service';
import { MalayalamTtsService } from './services/malayalam-tts.service';
import { AiCabBookingComponent } from './shared/components/ai-cab-booking/ai-cab-booking.component';
import { isCustomSoundFlagEnabled } from './core/helpers/notification-sound.helper';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { environment } from '../environments/environment';
import { ConfirmDialogService } from './services/confirm-dialog.service';
import { VoiceAssistantService } from './services/voice-assistant.service';
import { ActiveTripRedirectService } from './services/active-trip-redirect.service';

@Component({
  selector: 'logibids-root',
  imports: [
    RouterOutlet,
    CommonModule,
    ToastModule,
    GoogleMapComponent,
    UserHeadersComponent,
    ConfirmDialogModule,
    LiveChatWidgetModule,
    TrackingMapComponent,
    MapWidgetsComponent,
    DriverEnquiryComponent,
    DriverEnquiryImmediateComponent,
    AiCabBookingComponent,
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
})
export class AppComponent implements OnInit {
  ltr: boolean;
  rtl: boolean;
  url = '';
  isBrowser: boolean;
  isTrackingRoute = signal<boolean>(false);
  isDriverHome = signal<boolean>(false);
  showList = signal<boolean>(false);
  showListAll = signal<boolean>(false);
  showListImmediate = signal<boolean>(false);
  driverEnquiryReady = inject(EnquiryService).driverEnquiryReady;
  private _authService = inject(AuthService);
  private readonly malayalamTts = inject(MalayalamTtsService);
  private readonly malayalamPiperTts = inject(MalayalamPiperTtsService);
  userProfile = computed(() => this._authService.profileDetails());

  isDriverEnquiryVisible = computed(
    () => this.showListAll() || this.showList()
  );

  isDriverEnquiryActive = computed(
    () => this.isDriverEnquiryVisible() && this.driverEnquiryReady()
  );

  isDriverHomeAndProfile = computed(
    () => this.isDriverHome() && this.userProfile()?.type === 'DRIVER'
  );

  isDriverEnquiryTopVisible = computed(
    () => this.isDriverHomeAndProfile() && this.showList()
  );

  isDriverEnquiryImmediateVisible = computed(
    () => this.isDriverHomeAndProfile() && this.showListImmediate()
  );
  showAnim = false;
  assetsConst = assets;
  private AudioService = inject(AudioService);
  private globalClick = inject(GlobalClickService);

  private meta = inject(Meta);
  private title = inject(Title);
  private ngZone = inject(NgZone);

  private authService = inject(AuthService);
  private aiService = inject(aiService);
  private readonly voiceAssistant = inject(VoiceAssistantService);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly activeTripRedirect = inject(ActiveTripRedirectService);
  private microphonePromptOpen = false;

  readonly aiOpen = signal(false);

  accessToken = signal<string | null>(null);
  constructor(
    public translate: I18nService,
    private router: Router,
    private _messagingService: MessagingService,
    private swUpdateService: SwUpdateService,
    private _EnquiryService: EnquiryService,
    @Inject(PLATFORM_ID) private platformId: InjectionToken<object>
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.AudioService.register(
      'notification',
      this.assetsConst.payment_success
    );
    this.AudioService.register('driver', this.assetsConst.trackDriverAudio);
    this.AudioService.register(
      'driverHighAudio',
      this.assetsConst.trackDriverHighAudio
    );
    this.AudioService.register('logicalling', this.assetsConst.logicalling);
    // Initialize signals based on current URL
    const initialUrl = this.router.url;
    this.isTrackingRoute.set(initialUrl.startsWith('/sp-bids/tracking/'));
    this.isDriverHome.set(initialUrl.startsWith('/driver-home'));
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.isTrackingRoute.set(
          event.urlAfterRedirects.startsWith('/sp-bids/tracking/')
        );
        const driverHome = event.urlAfterRedirects.startsWith('/driver-home');
        this.isDriverHome.set(driverHome);
      }
    });

    this.bindSignals();

    effect(() => {
      const data = this._EnquiryService.driverEnquiry();
      if (data !== null) {
        this.showList.set(data.showList);
        this.showListAll.set(false);
      }
    });

    effect(() => {
      const data = this._EnquiryService.driverEnquiryAll();
      if (data !== null) {
        this.showListAll.set(data.showList);
        this.showList.set(false);
      }
    });

    effect(() => {
      const data = this._EnquiryService.driverEnquiryImmediate();
      if (data !== null) {
        this.showListImmediate.set(data.showList);
      }
    });

    effect(() => {
      if (!this.isBrowser) return;
      const token = localStorage['accessToken_sp'];
      if (this.authService.profileDetails()?.token) {
        this.accessToken.set(this.authService.profileDetails()?.token);
      } else {
        this.accessToken.set(token || undefined);
      }
    });

    effect(() => {
      if (!this.isBrowser) {
        return;
      }
      this.authService.profileDetails();
      this.accessToken();
      untracked(() => this.syncVoiceAssistant());
    });

    effect(() => {
      if (!this.isBrowser) {
        return;
      }
      if (!this.voiceAssistant.microphoneNeedsEnable()) {
        return;
      }
      untracked(() => {
        void this.promptMicrophonePermission();
      });
    });

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => this.syncVoiceAssistant(),
        error: () => undefined,
      });
  }

  /** Arms wake-word only for an authenticated app shell, not public/auth routes. */
  private syncVoiceAssistant(): void {
    if (!this.isBrowser || !environment.voiceWakeEnabled) {
      if (this.isBrowser) {
        this.voiceAssistant.disarm(false);
      }
      return;
    }
    const hasSession = !!(
      this.authService.profileDetails()?.token || this.accessToken()
    );
    const url = this.router.url;
    const onPublicAuthRoute =
      url.startsWith('/auth') || url.startsWith('/get-started');
    if (hasSession && !onPublicAuthRoute) {
      this.voiceAssistant.arm();
    } else {
      this.voiceAssistant.disarm(false);
    }
  }

  /**
   * Shows how to turn the microphone on in browser site settings.
   * Permission cannot be granted from the app once the browser has blocked it.
   */
  private async promptMicrophonePermission(): Promise<void> {
    if (this.microphonePromptOpen) {
      return;
    }

    this.microphonePromptOpen = true;
    try {
      const accepted = await this.confirmDialog.showMicrophonePermissionDialog(
        this.translate.translate('Attention'),
        this.translate.translate('microphoneAlert'),
        this.translate.translate('ok'),
        this.translate.translate('cancel')
      );

      if (accepted) {
        await this.voiceAssistant.retryMicrophoneFromUserGesture();
      } else {
        this.voiceAssistant.dismissMicrophonePrompt();
      }
    } finally {
      this.microphonePromptOpen = false;
    }
  }

  ngOnInit() {
    this.setupMeta(); // ✅ Ensuring SEO meta tags are set
    this.globalClick.init();
    this.activeTripRedirect.start();
    if (isPlatformBrowser(this.platformId)) {
      const lang = localStorage.getItem('language') || 'en';
      if (this.malayalamTts.shouldUseServerTts(lang)) {
        void this.malayalamTts.warmup();
      } else if (this.malayalamTts.isMalayalamLanguage(lang)) {
        void this.malayalamPiperTts.preload();
      }
      this._messagingService.requestPermission();
      this._messagingService.listenForFrontMessages();
      const message = this.translate.translate('notificationAlert');

      if (Notification.permission === 'denied') {
        alert(message);
      }
      this.swUpdateService.checkForUpdates();
      navigator.serviceWorker.addEventListener('message', event => {
        if (event.data && event.data.action === 'navigate') {
          const targetUrl = new URL(event.data.url);
          this.ngZone.run(() => {
            this.router.navigateByUrl(
              targetUrl.pathname + targetUrl.search + targetUrl.hash
            );
          });
          if (
            event.data.payload.notifications &&
            event.data.payload.notifications.length > 0
          ) {
            this._messagingService.sendMessage(
              event.data.payload.notifications
            );
          }
        } else if (
          event.data &&
          (event.data.action === 'playNotificationSound' ||
            event.data.action === 'refreshNotification')
        ) {
          const swData = event.data as Record<string, unknown>;
          this.ngZone.run(() => {
            this._messagingService.applyPushSound(
              isCustomSoundFlagEnabled(swData['custom_sound']),
              swData['type'],
              swData['messageId'],
              swData['sound_type']
            );
            this._messagingService.sendMessage({ action: 'refresh' });
          });
        }
      });
    }
  }

  private setupMeta(): void {
    this.title.setTitle('Logibids Service Provider');
    this.meta.addTags([
      { name: 'description', content: 'Find the best services on Logibids' },
      { property: 'og:title', content: 'Logibids Services' },
      { property: 'og:type', content: 'website' },
      { property: 'og:type', content: 'Service Provider' },
      { property: 'og:type', content: 'Driver' },
    ]);
  }

  private bindSignals(): void {
    effect(() => {
      const value = this.aiService.getData()();
      if (value !== null) {
        const prev = untracked(() => this.aiOpen());
        this.aiOpen.set(value);
      }
    });
  }
}
